import { useRef, useState, type SyntheticEvent } from 'react';
import type { ChartSpec } from '../../api/client';
import { useI18n } from '../../i18n';

/** Series colours --viz-1 … --viz-6 (styles/global.css); a chart never uses more. */
const MAX_SERIES = 6;

type Tip = { text: string; x: number; y: number };

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const m = v / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p;
}

/**
 * A chart drawn from the assistant's chart events: horizontal bars (grouped
 * when there are several series) or a line over ordered labels. Marks are thin,
 * grow from one baseline, and carry the series colour; text stays in text
 * colours. Every mark is focusable and has a tooltip, and the numbers are also
 * available as a table.
 */
export function Chart({ spec }: { spec: ChartSpec }) {
  const { t, tag } = useI18n();
  const box = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<Tip | null>(null);
  const series = spec.series.slice(0, MAX_SERIES);
  const many = series.length > 1;

  const fmt = (v: number) => {
    const n = new Intl.NumberFormat(tag, { maximumFractionDigits: spec.unit === 'mean' ? 2 : 1 }).format(v);
    return spec.unit === 'percent' ? `${n}%` : n;
  };

  function show(e: SyntheticEvent<Element>, text: string) {
    const host = box.current?.getBoundingClientRect();
    const el = e.currentTarget.getBoundingClientRect();
    if (!host) return;
    setTip({ text, x: el.left - host.left + el.width / 2, y: el.top - host.top });
  }
  const hide = () => setTip(null);
  const tipText = (i: number, k: number) => {
    const s = series[k];
    const value = fmt(s?.values[i] ?? 0);
    return many ? `${spec.labels[i]} · ${s?.name}: ${value}` : `${spec.labels[i]}: ${value}`;
  };

  return (
    <figure className="vz" aria-label={spec.title}>
      <figcaption className="vz-title">{spec.title}</figcaption>
      {many && (
        <ul className="vz-legend">
          {series.map((s, i) => (
            <li key={s.name}>
              <span className="vz-swatch" style={{ background: `var(--viz-${i + 1})` }} aria-hidden />
              {s.name}
            </li>
          ))}
        </ul>
      )}
      <div className="vz-plot" ref={box} onMouseLeave={hide}>
        {spec.kind === 'line' ? (
          <Line spec={{ ...spec, series }} fmt={fmt} show={show} hide={hide} tipText={tipText} />
        ) : (
          <Bars spec={{ ...spec, series }} fmt={fmt} show={show} hide={hide} tipText={tipText} />
        )}
        {tip && (
          <div className="vz-tip" role="status" style={{ left: tip.x, top: tip.y }}>
            {tip.text}
          </div>
        )}
      </div>
      {spec.note && <div className="vz-note">{spec.note}</div>}
      <details className="vz-data">
        <summary>{t('assistant.chart.data')}</summary>
        <table>
          <thead>
            <tr>
              <th>{t('assistant.chart.category')}</th>
              {series.map((s) => (
                <th key={s.name}>{s.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {spec.labels.map((label, i) => (
              <tr key={i}>
                <th scope="row">{label}</th>
                {series.map((s) => (
                  <td key={s.name}>{fmt(s.values[i] ?? 0)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}

interface PartProps {
  spec: ChartSpec;
  fmt: (v: number) => string;
  show: (e: SyntheticEvent<Element>, text: string) => void;
  hide: () => void;
  tipText: (i: number, s: number) => string;
}

function Bars({ spec, fmt, show, hide, tipText }: PartProps) {
  const many = spec.series.length > 1;
  const all = spec.series.flatMap((s) => s.values);
  const lo = Math.min(0, ...all);
  const hi = Math.max(0, ...all);
  const range = hi - lo || 1;
  const pct = (v: number) => ((v - lo) / range) * 100;
  const zero = pct(0);

  return (
    <div className={`vz-bars${many ? ' grouped' : ''}`} role="list">
      {spec.labels.map((label, i) => (
        <div
          key={i}
          className="vz-row"
          role="listitem"
          tabIndex={0}
          aria-label={spec.series.map((_, s) => tipText(i, s)).join(', ')}
          onMouseEnter={(e) => show(e, many ? spec.series.map((s) => `${s.name}: ${fmt(s.values[i] ?? 0)}`).join(' · ') : tipText(i, 0))}
          onFocus={(e) => show(e, many ? spec.series.map((s) => `${s.name}: ${fmt(s.values[i] ?? 0)}`).join(' · ') : tipText(i, 0))}
          onBlur={hide}
        >
          <span className="vz-label" title={label}>
            {label}
          </span>
          <span className="vz-tracks">
            {spec.series.map((s, k) => {
              const v = s.values[i] ?? 0;
              const a = pct(v);
              const left = Math.min(a, zero);
              return (
                <span key={k} className="vz-track">
                  <span
                    className={`vz-bar${v < 0 ? ' neg' : ''}`}
                    style={{ left: `${left}%`, width: `${Math.abs(a - zero)}%`, background: `var(--viz-${k + 1})` }}
                  />
                  {!many && (
                    <span className={`vz-value${v < 0 ? ' neg' : ''}`} style={v < 0 ? { right: `${100 - left}%` } : { left: `${Math.max(a, zero)}%` }}>
                      {fmt(v)}
                    </span>
                  )}
                </span>
              );
            })}
          </span>
        </div>
      ))}
    </div>
  );
}

const W = 320;
const H = 150;
const M = { l: 34, r: 34, t: 14, b: 24 };

function Line({ spec, fmt, show, hide, tipText }: PartProps) {
  const n = spec.labels.length;
  const hi = niceMax(Math.max(0, ...spec.series.flatMap((s) => s.values)));
  const x = (i: number) => M.l + (n <= 1 ? (W - M.l - M.r) / 2 : (i / (n - 1)) * (W - M.l - M.r));
  const y = (v: number) => M.t + (1 - v / hi) * (H - M.t - M.b);
  const ticks = [0, hi / 2, hi];
  const marks = n <= 31;
  // Label the first and last period, and the middle one on longer series, so labels never collide.
  const labelled = new Set([0, n - 1, ...(n >= 7 ? [Math.round((n - 1) / 2)] : [])]);

  return (
    <svg className="vz-line" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={spec.title}>
      {ticks.map((tk) => (
        <g key={tk}>
          <line className="vz-grid" x1={M.l} x2={W - M.r} y1={y(tk)} y2={y(tk)} />
          <text className="vz-axis" x={M.l - 6} y={y(tk)} textAnchor="end" dominantBaseline="middle">
            {fmt(tk)}
          </text>
        </g>
      ))}
      {spec.labels.map(
        (label, i) =>
          labelled.has(i) && (
            <text key={i} className="vz-axis" x={x(i)} y={H - 6} textAnchor={i === 0 && n > 1 ? 'start' : i === n - 1 && n > 1 ? 'end' : 'middle'}>
              {label.replace(/^week of /, '')}
            </text>
          ),
      )}
      {spec.series.map((s, k) => (
        <g key={k}>
          <polyline className="vz-path" fill="none" stroke={`var(--viz-${k + 1})`} points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(' ')} />
          {s.values.map(
            (v, i) =>
              (marks || i === n - 1) && <circle key={i} className="vz-dot" cx={x(i)} cy={y(v)} r={4} fill={`var(--viz-${k + 1})`} />,
          )}
          {s.values.length > 0 && (
            <text className="vz-end" x={x(n - 1) + 9} y={y(s.values[n - 1] ?? 0)} textAnchor="start" dominantBaseline="middle">
              {fmt(s.values[n - 1] ?? 0)}
            </text>
          )}
        </g>
      ))}
      {spec.labels.map((_, i) => (
        <rect
          key={i}
          className="vz-hit"
          x={x(i) - (W - M.l - M.r) / Math.max(n - 1, 1) / 2}
          y={M.t}
          width={(W - M.l - M.r) / Math.max(n - 1, 1)}
          height={H - M.t - M.b}
          tabIndex={0}
          aria-label={spec.series.map((_s, s) => tipText(i, s)).join(', ')}
          onMouseEnter={(e) => show(e, spec.series.map((_s, k) => tipText(i, k)).join(' · '))}
          onFocus={(e) => show(e, spec.series.map((_s, k) => tipText(i, k)).join(' · '))}
          onBlur={hide}
        />
      ))}
    </svg>
  );
}
